import { useEffect } from 'react';
import type { PatternMatchParams, PatternMatchState } from '../types';

interface Props {
  params: PatternMatchParams;
  puzzleState: PatternMatchState;
  onSolve: () => void;
  patchState: (updater: (prev: PatternMatchState) => PatternMatchState) => void;
}

export function PatternMatch({ params, puzzleState, onSolve, patchState }: Props) {
  const { gridIcons, correctSet, clueText } = params;
  const { selected, solved, lastWrong } = puzzleState;

  // Auto-check when 3 selected
  useEffect(() => {
    if (solved || selected.length !== 3) return;

    const correct =
      selected.length === correctSet.length &&
      correctSet.every(c => selected.includes(c));

    if (correct) {
      patchState(s => ({ ...s, solved: true, lastWrong: false }));
      onSolve();
    } else {
      patchState(s => ({ ...s, lastWrong: true }));
      // Clear wrong selection after animation
      const id = setTimeout(() => {
        patchState(s => ({ ...s, selected: [], lastWrong: false }));
      }, 900);
      return () => clearTimeout(id);
    }
  }, [selected, solved]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(icon: string) {
    if (solved || lastWrong) return;
    patchState(s => {
      if (s.selected.includes(icon)) {
        return { ...s, selected: s.selected.filter(i => i !== icon) };
      }
      if (s.selected.length >= 3) return s;
      return { ...s, selected: [...s.selected, icon] };
    });
  }

  if (solved) {
    return (
      <p className="puzzle-solved-msg">
        <i className="fa-solid fa-circle-check" aria-hidden="true" /> Pattern identified.
      </p>
    );
  }

  return (
    <div className="puzzle-pattern">
      <p className="puzzle-clue-text">{clueText}</p>
      <p className="puzzle-pattern__instruction">
        Select exactly 3 icons. ({selected.length}/3 selected)
      </p>
      <div className={`puzzle-pattern__grid${lastWrong ? ' puzzle-pattern__grid--wrong' : ''}`}>
        {gridIcons.map(icon => {
          const isSelected = selected.includes(icon);
          return (
            <button
              key={icon}
              className={`puzzle-pattern__cell${isSelected ? ' puzzle-pattern__cell--selected' : ''}`}
              type="button"
              aria-label={icon}
              aria-pressed={isSelected}
              onClick={() => toggle(icon)}
            >
              <i className={`fa-solid fa-${icon}`} aria-hidden="true" />
            </button>
          );
        })}
      </div>
      {lastWrong && (
        <p className="puzzle-error-msg">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Incorrect — try again.
        </p>
      )}
    </div>
  );
}
